/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { UnitPageComponent } from '@src/app/modules/unit/unit-page/unit-page.component';
import { PersonPageComponent } from '@src/app/pages/person-page/person-page.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { Frequency } from '@src/app/model/frequency';
import { DebtorsListService } from '@src/app/modules/txn/debtors-list/debtors-list.service';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { Field } from '@src/app/shared/field/Field';
import { Unit } from '@src/app/model/unit';
import { CurrencyPipe } from '@angular/common';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { MatDialog } from '@angular/material/dialog';
import { ActionService } from '@src/app/modules/crm/action.service';
import { HttpParams } from '@angular/common/http';
import { Action } from '@src/app/model/action';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { DebtorsListPageNavRoute } from '@src/app/routes/debtors-list-page-nav';

@Component({
    selector: 'app-debtors-list',
    imports: [FormPageComponent, CurrencyPipe],
    templateUrl: './debtors-list.component.html',
    styleUrl: './debtors-list.component.scss'
})
export class DebtorsListComponent extends AbstractPageComponent {
    debtorsListSvc = inject(DebtorsListService);

    dialog = inject(MatDialog);
    actionSvc = inject(ActionService);

    unitField = FormButtonComponent.makeTableButton('Unit', 'unitName', (item) => {
        this.dialog.open(UnitPageComponent,
            {
                data: {
                    id: item['unitId'],
                }
            }
        ).afterClosed().subscribe((u: Unit) => {
            if (u) {
                item['arrearsStageId'] = u.arrearsStageId;
                item['frequencyId'] = u.frequencyId;
            }            
            const p = new HttpParams().set('lastDoneFor', item['unitId'])
            this.actionSvc.get<Action>(false, p).subscribe( x => {
                if (x && x.length > 0) {
                    item['lastActionAt'] = x[0].completedAt;
                }
            })
        });
    });

    contactField = FormButtonComponent.makeTableButton('Contact', 'fullName', (item) => {
        this.dialog.open(PersonPageComponent,
            {
                data: {
                    id: item['ownerId'],
                }
            }
        ).afterClosed().subscribe((x) => {
            console.log(x);
        });
    });

    arrearsStageField = FormPicklistComponent.make('Arrears Stage', 'arrearsStageId', null, { items: Unit.ArrearsStages});    

    config = new FormConfig({
        navRoute: DebtorsListPageNavRoute,
        docPath: 'omc-books/service-charges/debtors-list',
        title: $localize`Debtors List`,
        help: $localize`Whatever`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                //FormButtonComponent.makeNavDetailButton('Unit', 'unitName', 'unitId', UnitPageComponent.navRoute),
                this.unitField,
                //FormButtonComponent.makeNavDetailButton('Contact', 'fullName', 'ownerId', PersonPageComponent.navRoute),
                this.contactField,
                FormTextComponent.make('Owner Name Search', 'ownerName', {visible:Field.noShow}),
                FormPhoneComponent.make('Phone', 'phone'),
                FormDateTimeComponent.make('Last Action', 'lastActionAt'),
                FormNumberComponent.make('Charges Outstanding', 'invoices').blankZero().override({ cellOpts: { style: 'text-align: center', width: '6em' } }),
                FormNumberComponent.makeCurrency('Total Charged', 'totalValue').blankZero().override({ cellOpts: { style: 'text-align: left' } }),
                FormNumberComponent.makeCurrency('Paid', 'totalPaid').blankZero().override({ cellOpts: { style: 'text-align: left' } }),
                FormNumberComponent.makeCurrency('Outstanding', 'outstanding').override({cellOpts: {style:'text-align: left'}}),
                FormNumberComponent.makeCurrency('Arrears', 'arrears').override({cellOpts: {style:'text-align: left'}}),
                FormNumberComponent.makePercent('Debtor Score', 'score').blankZero().override({ cellOpts: { style: 'text-align: left' } }),
                FormDateComponent.make('Last Payment', 'lastPay'),
                this.arrearsStageField,
                FormPicklistComponent.make('Frequency', 'frequencyId', null, { items: Frequency.PAYMENT_OPTIONS, }),
                FormDateComponent.make('asAtDate', 'asAtDate', { visible: Field.noShow })

            ],
            formLayout: LAYOUT_OPTIONS.singleCol,
        }),
        service: this.debtorsListSvc,
        mode: 'list',
        beforeList: this.beforeList.bind(this)
    });
    totalDebtors = 0;
    arrears = 0;

    beforeList(items: Unit[]) {
        this.totalDebtors = 0;
        this.arrears = 0

        items.forEach(u => {
            this.totalDebtors += +u.outstanding;
            this.arrears += +u.arrears;
        });
        return items;
    }

    dayBefore(from: string) {
        return DateHelper.adjustDate(from, -1);
    }

}
